import React, {useState} from "react";
import {Tooltip} from "antd";
import {DeleteOutlined, BankOutlined, FieldTimeOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import moment from "moment";
import {CustomButton} from "../../_defaultComponents/CustomButton/CustomButton";
import {useStore} from "../../../hooks/useStore";
import {ShiftTablePropTypes} from "./ShiftManagementComponentPropTypes";
import {TableContainer, ActionButtons} from "./ComponentStyles";
import ShiftUpdateModal from "./ShiftUpdateModal/ShiftUpdateModal";
import ShiftCompanyUpdateModal from "./ShiftCompanyUpdateModal/ShiftCompanyUpdateModal";
import {CourierLink} from "../../_defaultComponents/CourierLink/CourierLink";
import ShiftCourierUpdateModal from "./ShiftCourierUpdateModal/ShiftCourierUpdateModal";
import {CourierIcon} from "../../_defaultComponents/CourierIcon/CourierIcon";
import {EmptyMessage} from "../../_defaultComponents/EmptyMessage/EmptyMessage";

const ShiftTable = ({
    data,
    loading,
    setReloadData,
    currentPage,
    setCurrentPage,
    dataCount
}: ShiftTablePropTypes) => {
    const {t} = useTranslation(["shiftManagement"]);
    const stores = useStore();
    const [deleteConfirmLoading, setDeleteConfirmLoading] = useState(false);
    const [updateConfirmLoading, setUpdateConfirmLoading] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedCompanyUpdateItem, setSelectedCompanyUpdateItem] = useState(null);
    const [selectedShiftCourierUpdateItem, setSelectedShiftCourierUpdateItem] = useState(null);
    const path = window.location.pathname;

    const onClickDeleteButton = async id => {
        stores.appStore.openConfirmModal({
            title: t("modal.confirmTitle", {ns: "common"}),
            content: t("other.shiftDeleteConfirmContent", {ns: "common"}),
            onOk: async () => {
                setDeleteConfirmLoading(id);
                await stores.shiftStore.deleteShift({_id: id});
                setReloadData();
                setDeleteConfirmLoading(false);
            }
        });
    };

    const closeUpdateModal = (reloadData = false) => {
        if (reloadData) setReloadData();
        setSelectedItem();
    };

    const closeCompanyUpdateModal = (reloadData = false) => {
        if (reloadData) setReloadData();
        setSelectedCompanyUpdateItem();
    };

    const closeShiftCourierUpdateModal = (reloadData = false) => {
        if (reloadData) setReloadData();
        setSelectedShiftCourierUpdateItem();
    };

    const renderActionButtons = (id, item) => {
        const updateVisibility = moment(item.shiftEndTime) >= moment();
        const deleteVisibility =
            stores.meStore.checkRole("isAdmin") ||
            stores.meStore.checkRole("isOperationUser") ||
            stores.meStore.checkRole("isSOperationUser");
        const updateCompanyVisibility = moment(item.shiftStartTime).subtract(3, "m") > moment();
        const updateShiftCourierUpdateVisibility =
            moment(item.shiftStartTime).subtract(3, "m") > moment();
        return (
            <ActionButtons>
                {deleteVisibility && (
                    <Tooltip title={t("actions.delete", {ns: "common"})}>
                        <CustomButton
                            data-testid="deleteButton"
                            disabled={deleteConfirmLoading}
                            loading={deleteConfirmLoading === id}
                            onClick={() => onClickDeleteButton(id)}
                            icon={<DeleteOutlined />}
                        ></CustomButton>
                    </Tooltip>
                )}

                {item?.date && updateVisibility && (
                    <Tooltip title={t("actions.update", {ns: "common"})}>
                        <CustomButton
                            data-testid="updateButton"
                            disabled={deleteConfirmLoading}
                            loading={deleteConfirmLoading === id}
                            onClick={() => setSelectedItem(item)}
                            icon={<FieldTimeOutlined />}
                        ></CustomButton>
                    </Tooltip>
                )}

                {item?.date && updateCompanyVisibility && (
                    <Tooltip
                        title={t("actions.updateCompanyAndSubCompanyDedication", {ns: "common"})}
                    >
                        <CustomButton
                            data-testid="updateButton"
                            disabled={deleteConfirmLoading}
                            loading={deleteConfirmLoading === id}
                            onClick={() => setSelectedCompanyUpdateItem(item)}
                            icon={<BankOutlined />}
                        ></CustomButton>
                    </Tooltip>
                )}
                {item?.date && updateShiftCourierUpdateVisibility && (
                    <Tooltip title={t("actions.updateShiftCourier", {ns: "shiftManagement"})}>
                        <CustomButton
                            data-testid="updateShiftCourierButton"
                            disabled={updateConfirmLoading}
                            loading={updateConfirmLoading === id}
                            onClick={() => setSelectedShiftCourierUpdateItem(item)}
                            icon={<CourierIcon />}
                        ></CustomButton>
                    </Tooltip>
                )}
            </ActionButtons>
        );
    };

    return (
        <>
            <ShiftUpdateModal
                id={selectedItem?._id}
                courierName={selectedItem?.courier?.fullname}
                visibility={Boolean(selectedItem?._id)}
                date={moment(selectedItem?.date)}
                startTime={moment(selectedItem?.shiftStartTime)}
                endTime={moment(selectedItem?.shiftEndTime)}
                onCancel={closeUpdateModal}
            />
            <ShiftCompanyUpdateModal
                id={selectedCompanyUpdateItem?._id}
                courierName={selectedCompanyUpdateItem?.courier?.fullname}
                visibility={Boolean(selectedCompanyUpdateItem?._id)}
                onCancel={closeCompanyUpdateModal}
                companyId={
                    selectedCompanyUpdateItem?.company?._id
                        ? selectedCompanyUpdateItem.company._id
                        : "pool"
                }
                subCompanyId={
                    selectedCompanyUpdateItem?.subcompany?._id
                        ? selectedCompanyUpdateItem.subcompany._id
                        : ""
                }
                date={moment(selectedCompanyUpdateItem?.date)}
                startTime={moment(selectedCompanyUpdateItem?.shiftStartTime)}
                endTime={moment(selectedCompanyUpdateItem?.shiftEndTime)}
            />
            <ShiftCourierUpdateModal
                shift={selectedShiftCourierUpdateItem}
                visibility={Boolean(selectedShiftCourierUpdateItem?._id)}
                onCancel={closeShiftCourierUpdateModal}
            />
            <TableContainer
                pagination={{
                    total: dataCount,
                    current: currentPage,
                    onChange: async page => {
                        setCurrentPage(page);
                    },
                    hideOnSinglePage: true,
                    showSizeChanger: false
                }}
                rowKey="_id"
                dataSource={data}
                data-testid="shiftTable"
                locale={{
                    emptyText: <EmptyMessage />
                }}
                loading={loading}
                scroll={{x: "max-content"}}
                display={data.length > 0 ? "grid" : "flex"}
                dataTitles={[
                    data.length > 0 && t("fields.date", {ns: "common"}),
                    t("fields.courier", {ns: "common"}),
                    t("fields.identificationNumber", {ns: "common"}),
                    t("fields.city", {ns: "common"}),
                    t("fields.companyCode", {ns: "common"}),
                    t("fields.company", {ns: "common"}),
                    t("fields.subcompany", {ns: "common"}),
                    t("fields.subCompanyCode", {ns: "common"}),
                    t("fields.shiftStartTime", {ns: "common"}),
                    t("fields.shiftEndTime", {ns: "common"}),
                    t("fields.vacation", {ns: "common"}),
                    t("actions.action", {ns: "common"})
                ]}
            >
                <TableContainer.Column
                    dataIndex="date"
                    title={t("fields.date", {ns: "common"})}
                    render={date => (
                        <span data-testid="date">
                            {!date ? "-" : moment(date).format("DD.MM.YYYY")}
                        </span>
                    )}
                />
                <TableContainer.Column
                    dataIndex="courier"
                    title={t("fields.courier", {ns: "common"})}
                    render={courier => (
                        <span data-testid="date">
                            {stores.meStore.checkRole("courierDetail") ? (
                                <CourierLink target="_blank" to={`/couriers/${courier?._id}`}>
                                    {courier?.fullname}
                                </CourierLink>
                            ) : (
                                courier?.fullname
                            )}
                        </span>
                    )}
                />
                <TableContainer.Column
                    dataIndex="courier"
                    title={t("fields.identificationNumber", {ns: "common"})}
                    render={courier => <span data-testid="date">{courier?.tckn}</span>}
                />
                <TableContainer.Column
                    dataIndex="city"
                    title={t("fields.city", {ns: "common"})}
                    render={text => <span data-testid="date">{text?.capitalize()}</span>}
                />
                <TableContainer.Column
                    dataIndex="company"
                    title={t("fields.companyCode", {ns: "common"})}
                    render={company => <span data-testid="date">{company?.companyCode}</span>}
                />
                <TableContainer.Column
                    dataIndex="company"
                    title={t("fields.company", {ns: "common"})}
                    render={company => <span data-testid="date">{company?.companyName}</span>}
                />
                <TableContainer.Column
                    dataIndex="subcompany"
                    title={t("fields.subcompany", {ns: "common"})}
                    render={subcompany => <span data-testid="date">{subcompany?.name}</span>}
                />
                <TableContainer.Column
                    dataIndex="subcompany"
                    title={t("fields.subCompanyCode", {ns: "common"})}
                    render={subcompany => (
                        <span data-testid="date">{subcompany?.subCompanyCodeString}</span>
                    )}
                />
                <TableContainer.Column
                    dataIndex="shiftStartTime"
                    title={t("fields.shiftStartTime", {ns: "common"})}
                    render={date => (
                        <span data-testid="date">
                            {!date ? "-" : moment.utc(date).format("HH:mm")}
                        </span>
                    )}
                />
                <TableContainer.Column
                    dataIndex="shiftEndTime"
                    title={t("fields.shiftEndTime", {ns: "common"})}
                    render={date => (
                        <span data-testid="date">
                            {!date ? "-" : moment.utc(date).format("HH:mm")}
                        </span>
                    )}
                />
                <TableContainer.Column
                    dataIndex="onVacation"
                    title={t("fields.vacation", {ns: "common"})}
                    render={text => <span data-testid="date">{text}</span>}
                />
                {path !== "/panel/shift-management/snapshot" && (
                    <TableContainer.Column
                        title={t("actions.action", {ns: "common"})}
                        dataIndex="_id"
                        render={renderActionButtons}
                    />
                )}
            </TableContainer>
        </>
    );
};

export default ShiftTable;
